import {filter, map, shareReplay, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Timereport} from '../interfaces';
import {DataService} from '../../services/data.service';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {AuthService} from '../../services/auth.service';
import {AclUser} from '../../interfaces/app-user';
import {Activity, Budget, SubBudget} from '../../interfaces';

import moment, {Moment} from 'moment-timezone';

import {EllipsisPipe} from '../../shared/pipes/ellipsis.pipe';
import {ProjectNrPipe} from '../../shared/pipes/project-nr.pipe';

import {firestoreDateToJsDate} from '../../shared/utils/utils';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import {TimereportListTableService} from '../../services/timereport-list-table.service';
import {AppInfoDialogComponent} from '../../private/private.component';
import {Editor, NgxEditorModule} from 'ngx-editor';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatSelectModule} from '@angular/material/select';
import {MatButtonModule} from '@angular/material/button';
import {AsyncPipe, NgClass} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';



@Component({
    selector: 'timereport-add',
    standalone: true,
    templateUrl: './timereport-add.component.html',
    styleUrls: ['./timereport-add.component.scss'],
    imports: [
        NgxEditorModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        NgClass,
        EllipsisPipe,
        ProjectNrPipe,
        AsyncPipe,
        MatDialogModule,
        MatFormFieldModule
    ],
    providers: []

})
export class TimereportAddComponent implements OnInit, OnDestroy {

    public filteredUsers: Observable<Partial<AclUser>[]>;
    public filteredBudgets: Observable<Partial<Budget>[]>;
    public canChangeUser = false;
    public timereportForm: UntypedFormGroup; // our model driven form
    public submitted: boolean; // keep track on whether form is submitted
    public events: any[] = []; // use later to display form changes
    public singleSubBudget = true;
    public update = false;
    public selectedBudget;
    model: Timereport;
    budgets: BehaviorSubject<Partial<Budget>[]> = new BehaviorSubject([]);
    subbudgets$: Observable<Partial<SubBudget>[]>;
    activities: Observable<Partial<Activity>[]>;
    maxDate = new Date(); // cannot add an activity in the future
    users: BehaviorSubject<Partial<AclUser>[]> = new BehaviorSubject([]);
    editor: Editor;
    dataService: DataService = inject(DataService);
    private unsubscribe$ = new Subject();
    private _timeReport;
    // FORM Validation depends on user permission
    private descriptionFormControlValidators = [
        Validators.required,
        Validators.minLength(4)
    ];
    private loggedUser;

    constructor(private authService: AuthService,
                public dialog: MatDialog,
                private ellipsisPipe: EllipsisPipe,
                private projectNrPipe: ProjectNrPipe,
                private timereportListTableService: TimereportListTableService
    ) {
    }

    private _sentDates;

    @Input() set sentDates(sd: Set<number>) {
        console.log('TimereportAddComponent sentDates', sd);
        this._sentDates = sd;

        if (this.timereportForm) {
            this.timereportForm.get('date').updateValueAndValidity();
            if (this.timereportForm.get('date').invalid) {
                this.timereportForm.patchValue({
                    date: undefined
                }, {
                    emitEvent: false
                });
            }
        }

    }

    get timereport() {
        return this._timeReport;
    }

    @Input() set timereport(timereport: Timereport) {
        console.log('TimereportAddComponent set timereport', timereport);
        if (timereport) {

            this.timereportForm.get('activity').enable();
            this.timereportForm.get('subBudget').enable();

            this.update = true;
            this._timeReport = timereport;

            const theDate = moment.tz((timereport.date as firebase.firestore.Timestamp).toMillis(), 'Europe/Rome').set('hours', 14);

            console.log('patching:', new Date(), timereport.date, firestoreDateToJsDate(theDate));
            this.timereportForm.patchValue({
                creationDate: firestoreDateToJsDate(timereport.creationDate),
                user: timereport.user,
                date: firestoreDateToJsDate(theDate),
                description: timereport.description,
                budget: timereport.budget,
                subBudget: timereport.subBudget,
                hours: timereport.hours
            }, {
                emitEvent: true
            });

        } else {
            this._timeReport = null;
            this.initForm();
        }
    }

    ngOnInit() {
        this.editor = new Editor();

        this.authService.userHasPermission('app.timereport.anyone.write').pipe(
            takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                this.canChangeUser = val;
                if (val) {
                    this.descriptionFormControlValidators = undefined;

                    this.dateFilter = () => true;
                } else {
                    this.dateFilter = (d: Moment | null): boolean => {

                        if (!this._sentDates) {
                            return true;
                        }
                        return !this._sentDates.has(d.valueOf());
                    };
                }
            });

        this.dataService.getNotArchivedBudgetList().pipe(
            map(budgets => {
                const isSimpleUser = this.authService.currentUser.type.indexOf('user') !== -1 &&
                    this.authService.currentUser.type.length === 1;

                return budgets.filter(budget => !isSimpleUser || budget.status !== 'new')
                    .map(this._project({
                        budgetNr: 1,
                        id: 1,
                        project: 1,
                        company: 1,
                        export: 1,
                        description: 1,
                        status: 1
                    }));
            }),
            takeUntil(this.unsubscribe$))
            .subscribe(this.budgets);

        this.dataService.getActiveAppUsers()
            .pipe(
                map(users => {
                    return users.map((user) => {
                        return {
                            ...this._project({
                                id: 1,
                                email: 1,
                                displayName: 1,
                                personalName: 1,
                                googleUid: 1,
                                external: 1
                            })(user)
                        };
                    });
                }),
                takeUntil(this.unsubscribe$))
            .subscribe(this.users);


        this.setLoggedUser();
        this.initForm();


        if (this.authService.currentUser.type.indexOf('purch') !== -1) {
            this.maxDate.setFullYear(this.maxDate.getFullYear() + 10);
        }
    }

    public cancelTimereportEdit() {
        this._timeReport = null;
        this.timereportListTableService.cancelTimereportEdit();
        this.initForm();
    }

    public dateFilter = (d: Moment | null): boolean => {

        if (!this._sentDates) {
            return true;
        }
        return !this._sentDates.has(d.valueOf());
    }

    save(model: any | Timereport, isValid: boolean) {
        this.submitted = true;
        console.log('Model is', model, isValid);

        if (!isValid) {
            return;
        }

        const md = moment(model.date);
        const dateItaly = moment.tz({
            year: md.year(),
            month: md.month(),
            day: md.date()
        }, 'Europe/Rome');
        model.date = dateItaly.toDate();
        console.log('date to be saved in new timereport: ', model.date);


        let action;
        if (this._timeReport) {
            // UPDATE
            if (this._timeReport.actualCost !== undefined) {
                model.actualCost = this._timeReport.actualCost;
            } else {
                model.actualCost = Number.parseFloat(model.activity.cost);
            }
            console.log('UPDATE WITH', model);
            if (!model.creationDate) {
                model.creationDate = firebase.firestore.FieldValue.serverTimestamp();
            }

            action = this.dataService.updateTimereport(this._timeReport.id, model);
        } else {
            model.actualCost = Number.parseFloat(model.activity.cost);
            console.log('NEW WITH', model);
            model.creationDate = firebase.firestore.FieldValue.serverTimestamp();
            model['searchMode'] = 'quick';

            action = this.dataService.addTimereport(model);
        }
        action.then(() => {
            this.openDialog(true);
            this.cancelTimereportEdit();
            this.timereportListTableService.refreshSlowTable();
        }).catch(() => {
            this.openDialog(false);
        });
    }

    compareSubBudget(sb1, sb2) {
        return sb1.id === sb2.id;
    }

    compareActivity(att1, att2) {
        if (!att1 || !att2) {
            return false;
        }
        return att1.id === att2.id;
    }

    public displayFn(user: AclUser): string {
        if (user) {
            return user.email ? user.email : user.id;
        } else {
            return null;
        }
    }

    public budgetDisplayFn = (budget: Budget): string => {
        if (budget) {
            let ret = budget.budgetNr;
            if (budget.project) {
                ret = (budget.project.internal ? 'i' : '') + budget.project.projectNr + '-' + ret;
            }
            let cn;
            if (budget.company?.companyName) {
                cn = this.ellipsisPipe.transform(budget.company.companyName, 20);
                ret = cn + ' ' + ret;
            }
            return ret;
        } else {
            return null;
        }
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
        this.editor.destroy();
    }

    public openDialog(success: boolean) {
        let result: string;
        if (success) {
            result = 'Time Report successfully added';
        } else {
            result = 'Error while adding this time report';
        }
        this.dialog.open(AppInfoDialogComponent, {
            width: '350px',
            data: {
                infoMessage: result
            }
        });
    }

    getFormValidationErrors() {
        Object.keys(this.timereportForm.controls).forEach(key => {

            const controlErrors: ValidationErrors = this.timereportForm.get(key).errors;
            if (controlErrors != null) {
                Object.keys(controlErrors).forEach(keyError => {
                    console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
                });
            }
        });
    }

    private setLoggedUser() {
        this.loggedUser = this._project({
            id: 1,
            googleUid: 1,
            email: 1,
            displayName: 1,
            personalName: 1,
            external: 1
        })(this.authService.currentUser);
        for (const key in this.loggedUser) {
            if (!this.loggedUser[key]) {
                console.log('key not valid', key, this.loggedUser[key]);
                delete this.loggedUser[key];
            }
        }
    }

    private initForm() {
        const today = moment();
        const todayItaly = moment.tz({
            year: today.year(),
            month: today.month(),
            day: today.date()
        }, 'Europe/Rome');

        this.timereportForm = new UntypedFormGroup({
            budget: new UntypedFormControl('', [
                Validators.required,
                this.existsValidator
            ]),
            activity: new UntypedFormControl({value: null, disabled: true}, [
                Validators.required,
                this.existsValidator
            ]),
            subBudget: new UntypedFormControl({value: '', disabled: true}, [
                this.existsValidator
            ]),
            hours: new UntypedFormControl(0, Validators.required),
            creationDate: new UntypedFormControl(),
            date: new UntypedFormControl(this.dateFilter(todayItaly) ? todayItaly : undefined, Validators.required),
            user: new UntypedFormControl(this.loggedUser, [
                Validators.required,
                this.userExistsValidator
            ]),
            description: new UntypedFormControl('', this.descriptionFormControlValidators)
        });

        this.filteredUsers = this.timereportForm.get('user').valueChanges
            .pipe(
                startWith({} as AclUser),
                map(user => user && typeof user === 'object' ? user.email : String(user)),
                map(email => email ? this.filterUser(email) : this.users.value.slice())
            );

        this.filteredBudgets = this.timereportForm.get('budget').valueChanges
            .pipe(
                startWith({} as Budget),
                map(budget => budget && typeof budget === 'object' ? budget.budgetNr : String(budget)),
                map(budgetNr => budgetNr ? this.filterBudget(budgetNr) : this.budgets.value.slice())
            );

        const budgetChanges$ = this.timereportForm.get('budget').valueChanges.pipe(
            filter(value => typeof value === 'object' && value.id),
            tap(() => {
                this.timereportForm.get('subBudget').reset();
                this.timereportForm.get('activity').reset();
                this.timereportForm.get('activity').disable();
            }),
            shareReplay(1)
        );

        this.subbudgets$ = budgetChanges$.pipe(
            switchMap(budget => this.dataService.getReportSubBudget(budget.id)),
            tap(sbList => {
                console.log(sbList);
                this.timereportForm.get('subBudget').enable();
                if (sbList.length === 1) {
                    this.timereportForm.patchValue({subBudget: sbList[0]});
                    this.singleSubBudget = true;
                } else {
                    this.singleSubBudget = false;
                }
            })
        );

        this.subbudgets$.subscribe();

        const budgetActListFilter = ['Preventivi ed appuntamenti clienti', 'Marketing'];
        const prjmgrActList = ['Preventivi ed appuntamenti clienti', 'Gestione commessa', 'Marketing', 'Progettazione e programmi macchina', 'Formazione e spiegaz. comm.', 'Assenza'];
        const prdmgrActList = ['Gestione commessa', 'Progettazione e programmi macchina', 'Formazione e spiegaz. comm.', 'Assenza'];
        const prdmgrCatList = ['OFI', 'TRI', 'ASS'];

        this.activities = combineLatest([
            budgetChanges$,
            this.timereportForm.get('subBudget').valueChanges.pipe(startWith(this.timereportForm.get('subBudget').value)),
            this.dataService.getItemCategories()
        ]).pipe(
            filter(([budget, subBudget]) => budget?.id && subBudget?.id),
            tap(() => this.timereportForm.get('activity').reset()),
            switchMap(([budget, subBudget, cats]) => {
                this.selectedBudget = this.budgets.value.find(b => b.id === budget.id);
                const catMap = new Map<string, string>();
                cats.forEach(c => catMap.set(c.name, c.description));
                return this.dataService.getSBActivities(budget.id, subBudget.id, catMap);
            }),
            map((actList) => actList.map((act) => ({
                id: act.id, activity: act.activity, cost: act.cost, category: act.category
            })).filter(act => {
                if (this.selectedBudget && (['new', 'assigned'].includes(this.selectedBudget.status) &&
                    budgetActListFilter.indexOf(act.activity) === -1)) return false;

                const isSimpleUser = this.authService.currentUser.type.indexOf('user') !== -1 && this.authService.currentUser.type.length === 1;
                const isPrjMgr = this.authService.currentUser.type.indexOf('prjmgr') !== -1 && this.authService.currentUser.type.length === 1;
                const isReadOnlyPrjMgr = this.authService.currentUser.type.indexOf('readonlyprjmgr') !== -1 && this.authService.currentUser.type.length === 1;
                const isPrdMgr = this.authService.currentUser.type.indexOf('prdmgr') !== -1 && this.authService.currentUser.type.length === 1;

                if (isSimpleUser && (act.category === 'UFT' || act.category === 'AMM' || act.category === 'PRI')) return false;
                if (isPrjMgr && prjmgrActList.indexOf(act.activity) === -1) return false;
                if (isReadOnlyPrjMgr && prjmgrActList.indexOf(act.activity) === -1) return false;
                if (isPrdMgr && (prdmgrActList.indexOf(act.activity) === -1 && prdmgrCatList.indexOf(act.category) === -1)) return false;

                return true;
            })),
            tap(activities => {
                if (this.update && this._timeReport?.activity) {
                    const activityToSelect = activities.find(a => a.id === this._timeReport.activity.id);
                    if (activityToSelect) {
                        this.timereportForm.get('activity').patchValue(activityToSelect, {emitEvent: false});
                    }
                }
            }),
            tap(() => this.timereportForm.get('activity').enable())
        );
    }

    private existsValidator = (control): Record<string, any> => {
        return (typeof control.value === 'object') ? null : {'notExists': {value: control.value}};
    }

    private userExistsValidator = (control): Record<string, any> => {
        return (typeof control.value === 'object') ? null : {'userNotExists': {value: control.value}};
    }

    private filterUser(val: string): Partial<AclUser>[] {
        return this.users.value
            .filter(option => {
                if (option.email) {
                    return option.email.toLowerCase().indexOf(val.toLowerCase()) === 0;
                } else {
                    return option.id.toLowerCase().indexOf(val.toLowerCase()) === 0;
                }
            });
    }

    private filterBudget(val: string): Partial<Budget>[] {
        return this.budgets.value
            .filter(option => {
                if (option.budgetNr) {
                    let searchString = option.budgetNr;
                    if (option.project) {
                        searchString += this.projectNrPipe.transform(option);
                    }
                    if (option.company?.companyName) {
                        searchString += option.company?.companyName;
                    }

                    return searchString.toLowerCase().indexOf(val.toLowerCase()) !== -1;
                } else {
                    console.warn(`No budget number for ${option.id}`);
                    return option.id.toLowerCase().indexOf(val.toLowerCase()) === 0;
                }
            });
    }

    private _project(projection: object) {
        return function (object: any): Partial<any> {
            const returnObject = {};
            for (const prop in projection) {
                if (prop && (prop in object)) {
                    returnObject[prop] = object[prop];
                }
            }
            return returnObject;
        };
    }
}
